/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { IconChevronDown } from '@tabler/icons-react';
import { formatCurrency } from '../../../utils/global';
import { getProductPrice } from '../../../utils/shipping-utils';
import { TruncatedText } from '../../common';
import styles from './variantSelectionModal.module.css';
import CustomTooltip from '../../common/CustomTooltip/CustomTooltip';
import CustomSelect from '../../common/CustomSelect/CustomSelect';
import CloneIcon from '../../../assets/icons/CloneIcon';
import CloseCircle from '../../../assets/icons/CloseCircle';
import ProgressStepper from './ProgressStepper/ProgressStepper';
import TickIcon from '../../../assets/icons/TickIcon';
import useIsMobile from '../../../hooks/useIsMobile';

export default function ShipFreeProductVariantSelectionCard({
  id,
  name,
  variants,
  image,
  options,
  price,
  removeProduct,
  allOptions,
  setAllOptions,
  productIndex,
  cloneObject,
  hideBorder = false,
  quantities,
  setQuantities,
  cloneIconColor = '#7F38E9',
  cloneTooltip = 'Add new variant',
  activateCloneDisableLogic = false,
  quantityLabel = 'Quantity',
  defaultCurrency,
  disableQuantitySelection = false,
  removeCloneIcon = false,
}: any) {
  const isMobile = useIsMobile();
  const [currentOptionDropdown, setCurrentOptionDropdown] = useState<number | null>(null);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState<HTMLDivElement | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [stepperHeight, setStepperHeight] = useState<number>(0);
  const rightContainerRef = useRef<HTMLDivElement | null>(null);
  const optionDropdownRef = useRef<HTMLDivElement | null>(null);
  const shouldDisableClone = activateCloneDisableLogic && options?.length === 1 && options?.[0]?.options?.length === 1;

  useEffect(() => {
    if (rightContainerRef?.current) {
      setStepperHeight(rightContainerRef?.current?.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (quantities) {
      const { [id]: newQuantity } = quantities;
      setQuantity(newQuantity || 1);
    }
  }, [quantities, id]);

  const handleQuantityChange = (value: number) => {
    setQuantities({ ...quantities, [id]: value });
    setQuantity(value);
  };

  const variantPrice = getProductPrice({ id, variants, price }, allOptions);
  return (
    <div className={styles.shipFreeProductVariantsCardWrapper}>
      <ProgressStepper step={productIndex + 1} height={stepperHeight} />
      <div className={styles.shipFreeProductVariantsCard} ref={rightContainerRef}>
        <div className={styles.shipFreeProductVariantsCardContainers} style={{ border: hideBorder && 'none' }}>
          <div className={styles.leftContainer}>
            <div className={styles.productImageContainer}>
              <img src={image} alt={name} />
            </div>
            <div className={styles.productNameContainer}>
              <h3 className={styles.productName}>
                <TruncatedText text={name} maxLines={1} width={180} />
              </h3>
              <h4 className={styles.productCurrency}>
                {formatCurrency(Number(variantPrice || price || variants?.[0]?.price || 0), defaultCurrency)}
              </h4>
            </div>
            {!isMobile && (
              <div className={styles.closeIcon} tabIndex={0} role="button" onClick={() => removeProduct(id)}>
                <CustomTooltip title="Remove">
                  <div>
                    <CloseCircle color="var(--icon-critical)" style={{ marginTop: 6, cursor: 'pointer' }} />
                  </div>
                </CustomTooltip>
              </div>
            )}
          </div>
          <div className={styles.rightDetailsContainer}>
            {options?.[0]?.options?.[0] && (
              <div className={styles.variantOptionList}>
                {options?.[0]?.options?.[0] !== 'Default Title' &&
                  options?.map((option: any, index: number) => {
                    return (
                      <div key={option.id} className="list-item-margin-16">
                        <div className="flex flex-between items-center w-full mb-1">
                          <TruncatedText
                            text={
                              <>
                                Select {option?.name} <span className="text-critical">*</span>
                              </>
                            }
                            maxLines={1}
                            width={180}
                            className={`text-subdued ${isMobile ? 'text-xs' : 'text-sm'}`}
                          />{' '}
                          {index === 0 && !removeCloneIcon && (
                            <CustomTooltip title={shouldDisableClone ? 'No more variants to add' : cloneTooltip}>
                              <div
                                tabIndex={0}
                                role="button"
                                onClick={() => {
                                  if (shouldDisableClone) return;
                                  cloneObject(productIndex);
                                }}
                                className="cursor-pointer"
                              >
                                <CloneIcon color={shouldDisableClone ? 'var(--icon-disabled)' : cloneIconColor} />
                              </div>
                            </CustomTooltip>
                          )}
                        </div>
                        <div>
                          <div
                            tabIndex={0}
                            role="button"
                            className={styles.dropdownSelected}
                            onClick={(e: any) => {
                              setShowOptionsDropdown(e.currentTarget);
                              setCurrentOptionDropdown(index);
                            }}
                            ref={optionDropdownRef}
                            style={{
                              borderColor:
                                currentOptionDropdown === index ? 'var(--saral-purple)' : 'var(--border-default)',
                            }}
                          >
                            <span className={`${isMobile ? 'text-sm' : 'text-base'} text-default`}>
                              <TruncatedText
                                text={allOptions[id]?.[`option${index + 1}`] || `Choose ${option?.name}`}
                                maxLines={1}
                                width={isMobile ? 130 : 160}
                                style={{
                                  color: allOptions[id]?.[`option${index + 1}`]
                                    ? 'var(--text-default)'
                                    : 'var(--text-disabled)',
                                }}
                              />
                            </span>
                            <div
                              style={{
                                transform: currentOptionDropdown === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'all 0.2s ease-in-out',
                              }}
                              className="flex items-center flex-center"
                            >
                              <IconChevronDown color="var(--icon-subdued)" width={24} height={24} />
                            </div>
                          </div>
                          <Menu
                            anchorEl={showOptionsDropdown}
                            open={Boolean(currentOptionDropdown === index)}
                            PaperProps={{ sx: { width: optionDropdownRef?.current?.offsetWidth, padding: '0 8px' } }}
                            onClose={() => {
                              setShowOptionsDropdown(null);
                              setCurrentOptionDropdown(null);
                            }}
                          >
                            {option?.options?.map((v: any, i: number) => {
                              const isSelected = allOptions?.[id]?.[`option${index + 1}`] === v;
                              return (
                                <MenuItem
                                  disableRipple
                                  key={i}
                                  onClick={() => {
                                    setAllOptions({
                                      ...allOptions,
                                      [id]: { ...allOptions[id], [`option${index + 1}`]: v },
                                    });
                                    setShowOptionsDropdown(null);
                                    setCurrentOptionDropdown(null);
                                  }}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                    backgroundColor: isSelected ? 'var(--purple-p-10)' : 'transparent',
                                    padding: isMobile ? '6px' : '8px',
                                    borderRadius: '4px',
                                    minHeight: isMobile ? '32px' : '40px',

                                    '&:last-child': {
                                      marginBottom: 0,
                                    },
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: isMobile ? 14 : 16,
                                      color: isSelected ? 'var(--saral-purple)' : 'var(--text-default)',
                                    }}
                                  >
                                    {v}
                                  </span>
                                  {isSelected && <TickIcon color="var(--saral-purple)" width={20} height={20} />}
                                </MenuItem>
                              );
                            })}
                          </Menu>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {!disableQuantitySelection && quantityLabel && (
              <div className="flex items-center flex-between">
                <div>
                  {quantityLabel} <span className="text-critical">*</span>
                </div>

                <div className={styles.quantityContainer}>
                  <CustomSelect
                    dropDownElement={(setOpened) => {
                      return (
                        <div className={styles.quantityDropdown}>
                          {Array.from({ length: 15 }, (v, k) => ({ label: k + 1, value: k + 1 })).map((option) => (
                            <div
                              role="presentation"
                              className={styles.quantityItem}
                              style={{
                                backgroundColor: quantity === option.value ? 'var(--purple-p-10)' : '',
                                color: quantity === option.value ? 'var(--purple-p-400)' : '',
                              }}
                              key={option.value}
                              onClick={() => {
                                handleQuantityChange(option.value);
                                setOpened(false);
                              }}
                            >
                              <div>{option.label}</div>
                              {option.value === quantity && (
                                <TickIcon width={20} height={20} color="var(--purple-p-400)" />
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }}
                    selectLabel={
                      <span className={`${isMobile ? 'text-sm' : 'text-base'} text-default`}>{quantity}</span>
                    }
                    labelClassName={styles.quantityLabel}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
